import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod/v4";
import { SetupCard } from "@/components/setups/setup-card";
import { SetupFilters } from "@/components/setups/setup-filters";
import { Button } from "@/components/ui/button";

import { useFilteredSetups, useSetupFilterOptions } from "@/hooks/use-setups";
import { getFilteredSetups, getSetupFilterOptions } from "@/lib/setups";
import type { SetupFilters as SetupFiltersType, SetupListItem } from "@/lib/types";
import { cn } from "@/lib/utils";

// Helper: parse comma-separated string or array from URL params
const csvArray = z.preprocess((val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") return val.split(",").filter(Boolean);
  return undefined;
}, z.array(z.string()).optional());

const searchSchema = z.object({
  season: z.string().optional(),
  week: z.coerce.number().optional(),
  series: csvArray,
  car: csvArray,
  track: csvArray,
  driver: z.string().optional(),
  type: z
    .enum([
      "race",
      "qualifying",
      "wet",
      "safe",
      "endurance",
      "race_safe",
      "qualifying_safe",
      "endurance_safe",
    ])
    .optional(),
  freeOnly: z.coerce.boolean().optional(),
  sort: z.enum(["newest", "downloads", "lap_time"]).optional(),
  page: z.coerce.number().optional(),
});

export const Route = createFileRoute("/_auth/app/setups/")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ context: { queryClient }, deps: search }) => {
    const filters: SetupFiltersType = {
      season: search.season,
      week: search.week,
      series: search.series,
      car: search.car,
      track: search.track,
      driver: search.driver,
      type: search.type,
      freeOnly: search.freeOnly,
      sort: search.sort ?? "lap_time",
      page: search.page,
    };
    // Prefetch both queries in parallel during SSR — avoids cold start waterfall
    await Promise.all([
      queryClient.ensureQueryData({
        queryKey: ["setups", "filtered", filters],
        queryFn: () => getFilteredSetups({ data: filters }),
        staleTime: 2 * 60 * 1000,
      }),
      queryClient.ensureQueryData({
        queryKey: ["setups", "filterOptions", filters],
        queryFn: () => getSetupFilterOptions({ data: filters }),
        staleTime: 2 * 60 * 1000,
      }),
    ]);
  },
  component: BrowsePage,
});

const GRID_CLASSES = "grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

/**
 * Group setups by week_number, sorted by week descending (newest first).
 * When sort=lap_time, items inside each week are ordered by best_lap_time
 * ascending (nulls last) since we can't .order() across the related table.
 */
function groupByWeek(setups: SetupListItem[], sortByLapTime: boolean): [number, SetupListItem[]][] {
  const groups = new Map<number, SetupListItem[]>();
  for (const setup of setups) {
    const week = setup.week_number;
    const group = groups.get(week);
    if (group) {
      group.push(setup);
    } else {
      groups.set(week, [setup]);
    }
  }
  if (sortByLapTime) {
    for (const items of groups.values()) {
      items.sort((a, b) => {
        const av = a.latest_version?.best_lap_time;
        const bv = b.latest_version?.best_lap_time;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return Number(av) - Number(bv);
      });
    }
  }
  return [...groups.entries()].sort((a, b) => b[0] - a[0]);
}

function BrowsePage() {
  const navigate = useNavigate();
  const search = Route.useSearch();

  // Build filters from search params — default sort is "newest"
  const filters: SetupFiltersType = {
    season: search.season,
    week: search.week,
    series: search.series,
    car: search.car,
    track: search.track,
    driver: search.driver,
    type: search.type,
    freeOnly: search.freeOnly,
    sort: search.sort ?? "lap_time",
    page: search.page,
  };

  const { data, isLoading, isFetching, isError } = useFilteredSetups(filters);
  const { data: filterOptions } = useSetupFilterOptions(filters);

  // Show skeleton on initial load; show fade overlay when refetching with new filters
  const showSkeleton = isLoading;
  const showFetchingOverlay = !isLoading && isFetching;

  function handleFilterChange(newFilters: SetupFiltersType) {
    const cleaned: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(newFilters)) {
      if (v == null) continue;
      if (Array.isArray(v)) {
        if (v.length > 0) cleaned[k] = v.join(",");
      } else {
        cleaned[k] = v;
      }
    }
    navigate({
      to: "/app/setups",
      search: cleaned as any,
    });
  }

  // Accumulate setups across pages for infinite scroll
  const [allSetups, setAllSetups] = useState<SetupListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const total = data?.total ?? 0;
  const hasMore = allSetups.length < total;

  // Reset when filters change (but not page)
  const filterKey = JSON.stringify({ ...filters, page: undefined });
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      setAllSetups([]);
      setCurrentPage(1);
      prevFilterKey.current = filterKey;
    }
  }, [filterKey]);

  // Append new page data
  useEffect(() => {
    if (data?.setups) {
      if (currentPage === 1) {
        setAllSetups(data.setups);
      } else {
        setAllSetups((prev) => {
          const existingIds = new Set(prev.map((s) => s.id));
          const newItems = data.setups.filter((s: SetupListItem) => !existingIds.has(s.id));
          return [...prev, ...newItems];
        });
      }
    }
  }, [data?.setups, currentPage]);

  const weekGroups = groupByWeek(allSetups, filters.sort === "lap_time");

  // Intersection observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      handleFilterChange({ ...filters, page: nextPage });
    }
  }, [hasMore, isFetching, currentPage, filters]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="font-[Bebas_Neue] text-3xl text-foreground">Setups</h1>
        <p className="text-sm text-muted-foreground">Browse iRacing setups from the SZESE team</p>
      </div>

      {/* Filters */}
      {filterOptions && (
        <SetupFilters
          filters={filters}
          options={filterOptions}
          onFilterChange={handleFilterChange}
        />
      )}

      {/* Results count */}
      <AnimatePresence>
        {!showSkeleton && !isError && (
          <motion.p
            key="count"
            initial={{ opacity: 0 }}
            animate={{ opacity: showFetchingOverlay ? 0.5 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="text-sm text-muted-foreground"
          >
            {total} {total === 1 ? "setup" : "setups"} found
          </motion.p>
        )}
      </AnimatePresence>

      {/* Loading skeleton — only on initial load (no cached data) */}
      <AnimatePresence>
        {showSkeleton && (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={GRID_CLASSES}
          >
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-xl border border-border/50 bg-card">
                <div className="aspect-[16/9] rounded-t-xl bg-muted" />
                <div className="space-y-2 p-3">
                  <div className="h-4 w-3/4 rounded bg-muted" />
                  <div className="h-3 w-1/2 rounded bg-muted" />
                  <div className="h-3 w-1/3 rounded bg-muted" />
                  <div className="flex gap-1.5 pt-1">
                    <div className="h-3 w-10 rounded bg-muted" />
                    <div className="h-3 w-10 rounded bg-muted" />
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error state */}
      {!showSkeleton && isError && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-destructive/30 bg-destructive/5 py-16"
        >
          <p className="text-destructive">Failed to load setups. Please try again.</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-3 text-xs"
            onClick={() => window.location.reload()}
          >
            Reload page
          </Button>
        </motion.div>
      )}

      {/* Results grouped by week */}
      {!showSkeleton && !isError && data && data.setups.length > 0 && (
        <motion.div
          key={JSON.stringify(filters)}
          initial={{ opacity: 0 }}
          animate={{ opacity: showFetchingOverlay ? 0.4 : 1 }}
          transition={{ duration: 0.25 }}
          className={cn("space-y-6", showFetchingOverlay && "pointer-events-none")}
        >
          {weekGroups.map(([week, setups], groupIndex) => (
            <motion.div
              key={week}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: groupIndex * 0.08 }}
            >
              <div className="mb-3 flex items-center gap-3">
                <h2 className="font-[Bebas_Neue] text-3xl text-blue-500">Week {week}</h2>
                <div
                  className="relative h-14 flex-1"
                  style={{
                    backgroundImage: "url(/kalocsai-strip-tile.webp)",
                    backgroundRepeat: "repeat-x",
                    backgroundSize: "400px 140%",
                    backgroundPosition: "center",
                    opacity: 0.12,
                    maskImage:
                      "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent), linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                    WebkitMaskImage:
                      "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent), linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                    maskComposite: "intersect",
                    WebkitMaskComposite: "destination-in",
                  }}
                />
                <span className="text-xs text-muted-foreground">
                  {setups.length} {setups.length === 1 ? "setup" : "setups"}
                </span>
              </div>
              <div className={GRID_CLASSES}>
                {setups.map((setup: SetupListItem, i) => (
                  <motion.div
                    key={setup.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: i * 0.03 }}
                  >
                    <SetupCard setup={setup} />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Empty state */}
      {!showSkeleton && !isFetching && !isError && data && data.setups.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/50 py-16"
        >
          <p className="text-muted-foreground">No setups match your filters</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-3 text-xs"
            onClick={() => {
              setAllSetups([]);
              setCurrentPage(1);
              handleFilterChange({});
            }}
          >
            Clear filters
          </Button>
        </motion.div>
      )}

      {/* Infinite scroll sentinel */}
      {hasMore && (
        <div ref={loadMoreRef} className="flex items-center justify-center py-8">
          {isFetching && (
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
          )}
        </div>
      )}
      {!hasMore && allSetups.length > 0 && (
        <p className="py-4 text-center text-xs text-muted-foreground/50">
          All {total} setups loaded
        </p>
      )}
    </div>
  );
}
